/**
 * Tri-state theme controller: `light` | `dark` | `system`.
 *
 * Persists the user's explicit choice to `localStorage` under
 * `theme-preference`. When set to `system` the controller listens to
 * `prefers-color-scheme` and toggles the `.dark` class on the root
 * `<html>` element in real time — which is what every shadcn primitive
 * keys off via Tailwind's `dark:` variant.
 *
 * To consume from a React component:
 *
 *     const { resolved, preference, setPreference } = useTheme();
 *     resolved          // "light" | "dark" — the currently applied mode
 *     preference        // "light" | "dark" | "system" — the user choice
 *     setPreference(p)  // persist + apply
 */
import * as React from "react";

export type ThemePreference = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

const STORAGE_KEY = "theme-preference";

function readStoredPreference(): ThemePreference {
  if (typeof window === "undefined") return "system";
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === "light" || raw === "dark" || raw === "system") return raw;
  } catch {
    // localStorage may be blocked (Safari private, etc.) — fall through.
  }
  return "system";
}

function osPrefersDark(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

function applyResolvedThemeToRoot(resolved: ResolvedTheme): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (resolved === "dark") {
    root.classList.add("dark");
  } else {
    root.classList.remove("dark");
  }
  // Mirror onto a data attribute so non-Tailwind libs can react too.
  root.dataset.theme = resolved;
}

/** Compute the effective theme given a preference + the current OS hint. */
export function resolveTheme(pref: ThemePreference): ResolvedTheme {
  if (pref === "light" || pref === "dark") return pref;
  return osPrefersDark() ? "dark" : "light";
}

interface ThemeContextValue {
  preference: ThemePreference;
  resolved: ResolvedTheme;
  setPreference: (p: ThemePreference) => void;
}

const ThemeContext = React.createContext<ThemeContextValue | undefined>(
  undefined,
);

// ───────────────────────────────────────────────────────────────────────────
// DARK MODE SOFT-DISABLE (2026-02)
// The user asked to abandon the dark mode rollout for now while keeping all
// `dark:` Tailwind classes and `useTheme()` consumers intact, so re-enabling
// later is a single-commit revert. We force `light` everywhere:
//   • `preference` and `resolved` are pinned to "light"
//   • the `.dark` class is stripped from <html> on mount
//   • any persisted "dark" / "system" preference in localStorage is cleared
//   • `setPreference` is a no-op (UI toggle is also hidden from TopNavBar)
// To re-enable dark mode later, revert this block back to the prior
// state-driven implementation. The legacy helpers (`readStoredPreference`,
// `resolveTheme`, etc.) are intentionally kept above for that reason.
// ───────────────────────────────────────────────────────────────────────────
export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // Hard-pin to light, regardless of OS or stored preference.
  const preference: ThemePreference = "light";
  const resolved: ResolvedTheme = "light";

  React.useEffect(() => {
    applyResolvedThemeToRoot("light");
    // Scrub any leftover dark preference from previous sessions.
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore — localStorage may be blocked
    }
  }, []);

  const setPreference = React.useCallback((_next: ThemePreference) => {
    // No-op while dark mode is soft-disabled.
  }, []);

  const value = React.useMemo<ThemeContextValue>(
    () => ({ preference, resolved, setPreference }),
    [setPreference],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = (): ThemeContextValue => {
  const ctx = React.useContext(ThemeContext);
  if (!ctx) {
    throw new Error("useTheme must be used within <ThemeProvider>");
  }
  return ctx;
};
